// File: src/components/FormulaBuilderModal.js
import React, { useState, useEffect } from 'react'
import './FormulaBuilderModal.css'

// Common mathjs functions (including our conditional helper)
const FUNCTIONS = [
  'if',  // our special conditional
  'abs','ceil','floor','round','log','exp','sqrt',
  'sin','cos','tan','min','max','sum','mean'
]

// Operators for generic insertion
const OPERATORS = ['+','-','*','/','^','%']

export default function FormulaBuilderModal({
  field,
  existingExpr = '',
  fieldsList = [],
  labels = {},
  onSave,
  onCancel
}) {
  const [func, setFunc]           = useState('')
  const [expr, setExpr]           = useState(existingExpr)

  // Guided 'if' builder state
  const [condLeft, setCondLeft]   = useState('')
  const [condOp, setCondOp]       = useState('')
  const [condRight, setCondRight] = useState('')
  const [thenExpr, setThenExpr]   = useState('')
  const [elseExpr, setElseExpr]   = useState('')

  // When you select a normal function, append "func()" and reset
  useEffect(() => {
    if (func && func !== 'if') {
      setExpr(e => e + `${func}()`)
      setFunc('')
    }
  }, [func])

  // Insert an if(...) snippet when you click the button
  function insertIf() {
    if (!condLeft || !condOp || !condRight.trim()) {
      return alert('Complete the condition: field, operator, and value.')
    }
    if (!thenExpr.trim() || !elseExpr.trim()) {
      return alert('Fill both THEN and ELSE expressions.')
    }
    // raw variable names—no braces
    const snippet = `if(${condLeft}${condOp}${condRight.trim()},${thenExpr.trim()},${elseExpr.trim()})`
    setExpr(e => e + snippet)

    // reset guided inputs
    setCondLeft(''); setCondOp(''); setCondRight('')
    setThenExpr(''); setElseExpr('')
    setFunc('')
  }

  // Validate and save
  function handleSave() {
    // extract all bare words that look like variables
    const referenced = [...new Set(
      (expr.match(/\b([a-zA-Z]\w*)\b/g)||[])
        .filter(tok => fieldsList.includes(tok))
    )]
    const unknowns = referenced.filter(k => !fieldsList.includes(k))
    if (unknowns.length) {
      return alert(`Unknown field(s): ${unknowns.join(', ')}`)
    }
    onSave(expr)
  }

  return (
    <div className="fbm-overlay">
      <div className="fbm-modal">
        <h3>Edit formula for “{labels[field]||field}”</h3>

        {/* Function picker */}
        <div className="fbm-row">
          <label>Function</label>
          <select
            value={func}
            onChange={e => setFunc(e.target.value)}
          >
            <option value="">── Select Function ──</option>
            {FUNCTIONS.map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>

        {/* Guided IF builder */}
        {func === 'if' && (
          <>
            <div className="fbm-row">
              <label>Condition</label>
              <select
                value={condLeft}
                onChange={e => setCondLeft(e.target.value)}
              >
                <option value="">Field…</option>
                {fieldsList.map(k => (
                  <option key={k} value={k}>{labels[k]||k}</option>
                ))}
              </select>
              <select
                value={condOp}
                onChange={e => setCondOp(e.target.value)}
              >
                <option value="">Operator…</option>
                {['==','!=','>','<','>=','<='].map(op => (
                  <option key={op} value={op}>{op}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="value or fieldName"
                value={condRight}
                onChange={e => setCondRight(e.target.value)}
              />
            </div>

            <div className="fbm-row">
              <label>Then</label>
              <input
                type="text"
                placeholder="value or fieldName"
                value={thenExpr}
                onChange={e => setThenExpr(e.target.value)}
              />
            </div>

            <div className="fbm-row">
              <label>Else</label>
              <input
                type="text"
                placeholder="value or fieldName"
                value={elseExpr}
                onChange={e => setElseExpr(e.target.value)}
              />
            </div>

            <div className="fbm-row">
              <button type="button" onClick={insertIf}>
                Insert if(…) snippet
              </button>
            </div>
          </>
        )}

        {/* Generic field insertion */}
        <div className="fbm-row">
          <label>Field</label>
          <select
            value=""
            onChange={e => setExpr(prev => prev + e.target.value)}
          >
            <option value="">── Select Field ──</option>
            {fieldsList.map(k => (
              <option key={k} value={k}>{labels[k]||k}</option>
            ))}
          </select>
        </div>

        <div className="fbm-row">
          <label>Operator</label>
          <select
            value=""
            onChange={e => setExpr(prev => prev + e.target.value)}
          >
            <option value="">── Operator ──</option>
            {OPERATORS.map(o => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>

        {/* Final expression editor */}
        <div className="fbm-row">
          <label>Expression</label>
          <textarea
            rows={3}
            value={expr}
            onChange={e => setExpr(e.target.value)}
            placeholder="e.g. costPerKgFobExfactoryeuro + costPerKgInlandFreighteuro"
          />
        </div>

        <div className="fbm-actions">
          <button type="button" onClick={handleSave}>Save</button>
          <button type="button" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  )
}
